import { format, getTimezoneOffset, toZonedTime } from 'date-fns-tz';
import { formatInTimeZone } from 'date-fns-tz';
import { parse, parseISO } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';

export const DEFAULT_DATE_TIME_FORMAT = 'dd/MMM/yyyy hh:mm a zzz';
export const DEFAULT_DATE_TIME_FORMAT_WITHOUT_ZONE = 'dd/MMM/yyyy hh:mm a';
export const DEFAULT_DATE_FORMAT = 'dd/MMM/yyyy';
export const DEFAULT_TIME_FORMAT = 'hh:mm a zzz';

export function getComputerFormattedTime(date: Date | string | undefined | null, dateFormat = DEFAULT_DATE_TIME_FORMAT): string {
    if (!date) return '-';

    const dateValue = typeof date === 'string' ? new Date(date) : date;

    return format(dateValue, dateFormat, { locale: enUS });
}

export function getUtcFormattedTime(date: Date | string | undefined, dateFormat = DEFAULT_DATE_TIME_FORMAT): string {
    if (!date) return '-';

    const dateValue = typeof date === 'string' ? new Date(date) : date;

    return formatInTimeZone(dateValue, 'UTC', dateFormat);
}

export function getTimezoneFormattedTime(date: Date | string | undefined, timeZone?: string, dateFormat = DEFAULT_DATE_TIME_FORMAT): string {
    if (!date) return '';
    if (!timeZone) return '';

    const dateValue = typeof date === 'object' ? date.toISOString() : date;

    return formatInTimeZone(dateValue, timeZone, dateFormat, { locale: enUS });
}

/**
 * Apply timezone offset (in milliseconds)
 * to the provided Date
 */
export function withTimezoneOffset(date: Date, tz: string) {
    const offsetMs = getTimezoneOffset(tz, date);

    return new Date(date.valueOf() + offsetMs);
}

export const makeDateKey = (date: Date) => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');

    return `${year}-${month}-${day}`;
};

export function isSameDayInTimezone(date: Date, date2: Date, timezone: string) {
    const dateInTz = toZonedTime(date, timezone);
    const dateInTzKey = makeDateKey(dateInTz);

    const date2InTz = toZonedTime(date2, timezone);
    const date2InTzKey = makeDateKey(date2InTz);

    return dateInTzKey === date2InTzKey;
}

export function parseDateFromString(dateString: string, dateFormat?: string): Date {
    if (!dateFormat) {
        return parseISO(dateString);
    } else {
        return parse(dateString, dateFormat, new Date());
    }
}

export const formatInISOString = (date: Date) => {
    const formattedDate = format(date, 'yyyy-MM-dd');
    const formattedTime = format(date, 'HH:mm:ss');

    return `${formattedDate}T${formattedTime}.000Z`;
};

export const formatToMinutesAndHours = (duration: number) => {
    if (!duration) {
        return '0m';
    }

    const hours = Math.floor(duration / 60);
    const minutes = Math.round(duration % 60);
    const hourWithMinutes = minutes ? `${hours}h ${minutes}m` : `${hours}h`;

    return hours ? hourWithMinutes : `${minutes}m`;
};

export const getLongFormatOfTimezone = (timezone: string) => {
    switch (timezone) {
        case 'EST':
        case 'EDT':
            return 'Eastern';
        case 'CST':
        case 'CDT':
            return 'Central';
        case 'MST':
        case 'MDT':
            return 'Mountain';
        case 'PST':
        case 'PDT':
            return 'Pacific';
        case 'AKST':
        case 'AKDT':
            return 'Alaska';
        case 'HST':
            return 'Hawaii';
        default:
            return timezone;
    }
};
